import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import EventTopBar from '../components/eventDetails/EventTopBar';
import { paymentStatusStyles, registrationDialogStyles, topBarStyles } from '../components/eventDetails/eventDetailsStyles';
import amexLogo from '../assets/amex.png';
import masterCardLogo from '../assets/mc_logo.svg';
import napsLogo from '../assets/naps.svg';
import napsWordmark from '../assets/naps_logo.png';
import visaLogo from '../assets/visa_logo.svg';
import { fetchManualPayment, initiateManualPayment } from '../services/api';
import {
  getPaymentServerCallbackUrl,
  PAYMENT_SERVER_CANCEL_PATH,
  PAYMENT_SERVER_COMPLETE_PATH,
} from '../routes/paymentRoutes';

const paymentOptions = [
  {
    value: 'local_debit_apple_pay',
    title: 'Local Debit Card / Apple Pay',
    description: 'Use a local debit card or Apple Pay for a fast checkout.',
    badge: 'Debit',
    logos: [
      { src: napsLogo, alt: 'NAPS' },
      { src: napsWordmark, alt: 'NAPS network' },
    ],
  },
  {
    value: 'credit_card_apple_pay',
    title: 'Credit Card / Apple Pay',
    description: 'Pay with a credit card or Apple Pay through secure checkout.',
    badge: 'Credit',
    logos: [
      { src: visaLogo, alt: 'Visa' },
      { src: masterCardLogo, alt: 'Mastercard' },
      { src: amexLogo, alt: 'American Express' },
    ],
  },
];

const getCheckoutProvider = (paymentType) => (
  paymentType === 'local_debit_apple_pay' ? 'qnb_all' : 'cybersource'
);

const getStatusDisplayLabel = (value) => {
  if (!value) return '-';
  return String(value)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getHostedCheckoutUrl = (purchaseResponse) => (
  purchaseResponse.checkout_url
  || purchaseResponse.payment_url
  || purchaseResponse.redirect_url
  || purchaseResponse.hosted_checkout_url
  || purchaseResponse.url
);

const appendCheckoutField = (form, name, value) => {
  if (!name || value === undefined || value === null) return;
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  input.value = typeof value === 'object' ? JSON.stringify(value) : String(value);
  form.appendChild(input);
};

const submitHostedCheckoutForm = (checkoutUrl, checkoutFields) => {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = checkoutUrl;
  form.style.display = 'none';

  const fieldEntries = Array.isArray(checkoutFields)
    ? checkoutFields.map((field) => [field.name || field.field_name || field.key, field.value])
    : Object.entries(checkoutFields || {});

  fieldEntries.forEach(([name, value]) => {
    if (Array.isArray(value)) {
      value.forEach((fieldValue) => appendCheckoutField(form, name, fieldValue));
      return;
    }
    appendCheckoutField(form, name, value);
  });

  document.body.appendChild(form);
  form.submit();
};

const DetailRow = ({ label, value }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 2 }} justifyContent="space-between" sx={paymentStatusStyles.detailRow}>
    <Typography variant="body2" color="text.secondary" sx={paymentStatusStyles.detailLabel}>{label}</Typography>
    <Typography variant="body2" sx={paymentStatusStyles.detailValue}>{value || '-'}</Typography>
  </Stack>
);

const CompletePendingPaymentPage = () => {
  const { purchaseRef = '' } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [manualPurchase, setManualPurchase] = useState(null);
  const [paymentType, setPaymentType] = useState('');
  const [paymentTypeError, setPaymentTypeError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!purchaseRef) {
      setLoading(false);
      setError('Missing purchase reference in URL.');
      return;
    }

    let ignore = false;

    const loadManualPurchase = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchManualPayment(purchaseRef);
        if (ignore) return;
        setManualPurchase(response.data || null);
      } catch (requestError) {
        if (ignore) return;
        const message = requestError.response?.data?.detail || requestError.response?.data?.message || requestError.message;
        setError(message || 'Unable to load pending purchase details.');
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadManualPurchase();

    return () => {
      ignore = true;
    };
  }, [purchaseRef]);

  const attendees = useMemo(() => {
    if (Array.isArray(manualPurchase?.attendees)) return manualPurchase.attendees;
    if (manualPurchase?.attendee) return [manualPurchase.attendee];
    return [];
  }, [manualPurchase]);

  const handleProceedToPayment = async () => {
    if (!paymentType) {
      setPaymentTypeError('Please select a payment method.');
      return;
    }

    setPaymentTypeError('');
    setSubmitError('');
    setSubmitting(true);

    try {
      const payload = {
        payment_method: 'online',
        payment_type: paymentType,
        checkout_provider: getCheckoutProvider(paymentType),
        return_url: getPaymentServerCallbackUrl(PAYMENT_SERVER_COMPLETE_PATH),
        cancel_url: getPaymentServerCallbackUrl(PAYMENT_SERVER_CANCEL_PATH),
      };

      const response = await initiateManualPayment(purchaseRef, payload);
      const checkoutData = response.data || {};
      const checkoutUrl = getHostedCheckoutUrl(checkoutData);
      const checkoutFields = checkoutData.checkout_fields || checkoutData.hosted_checkout_fields || checkoutData.fields;

      if (!checkoutUrl) {
        throw new Error('Payment checkout URL is missing from the response.');
      }

      if (checkoutFields && (Array.isArray(checkoutFields) ? checkoutFields.length : Object.keys(checkoutFields).length)) {
        submitHostedCheckoutForm(checkoutUrl, checkoutFields);
        return;
      }

      window.location.assign(checkoutUrl);
    } catch (requestError) {
      const message = requestError.response?.data?.detail || requestError.response?.data?.message || requestError.message;
      setSubmitError(message || 'Unable to start manual payment checkout.');
      setSubmitting(false);
    }
  };

  const ticketDetails = manualPurchase?.ticket_details;
  const statusLabel = manualPurchase?.payment_status || manualPurchase?.status || '-';
  const amountLabel = manualPurchase?.amount ? `${manualPurchase.amount} ${manualPurchase.currency || 'QAR'}` : '-';
  const normalizedStatus = String(manualPurchase?.payment_status || manualPurchase?.status || '').toLowerCase();
  const isAlreadyPaid = ['paid', 'completed', 'complete', 'success', 'successful'].includes(normalizedStatus);
  const isMultiAttendee = attendees.length > 1;

  return (
    <Box sx={paymentStatusStyles.page}>
      <Box sx={topBarStyles.shell}>
        <EventTopBar />
      </Box>

      <Container maxWidth="md" sx={paymentStatusStyles.container}>
        <Card sx={paymentStatusStyles.card}>
          <CardContent sx={paymentStatusStyles.cardContent}>
            <Stack spacing={3} sx={paymentStatusStyles.contentStack}>
              <Typography variant="h4" sx={paymentStatusStyles.title({ color: '#065f46' })}>Complete Pending Payment</Typography>
              <Typography color="text.secondary">
                Choose a payment method to complete this pending ticket purchase.
              </Typography>

              {loading && <CircularProgress size={42} />}
              {!loading && error && <Alert severity="error" sx={paymentStatusStyles.errorAlert}>{error}</Alert>}

              {!loading && !error && manualPurchase && (
                <Stack spacing={2.5} sx={{ width: '100%' }}>
                  <Stack spacing={0.75} alignItems="center" sx={paymentStatusStyles.amountPanel({ color: '#065f46', bg: '#ecfdf5', border: '#a7f3d0' })}>
                    <Typography variant="caption" color="text.secondary" sx={paymentStatusStyles.amountLabel}>Amount</Typography>
                    <Typography variant="h4" sx={paymentStatusStyles.amount({ color: '#065f46' })}>{amountLabel}</Typography>
                    <Typography variant="body2" color="text.secondary">{`Status: ${getStatusDisplayLabel(statusLabel)}`}</Typography>
                  </Stack>

                  {isAlreadyPaid && (
                    <Alert severity="success">
                      This purchase is already paid. No further payment action is required.
                    </Alert>
                  )}

                  <Box sx={paymentStatusStyles.detailsPanel}>
                    <Stack spacing={0.5} alignItems="center" sx={paymentStatusStyles.detailsHeader}>
                      <Typography variant="subtitle1" sx={paymentStatusStyles.detailsTitle}>Purchase Details</Typography>
                    </Stack>
                    <Divider />
                    <DetailRow label="Purchase Reference" value={manualPurchase.purchase_ref || manualPurchase.reference || purchaseRef} />
                    <DetailRow label="Event" value={ticketDetails?.event_title} />
                    <DetailRow label="Occurrence" value={ticketDetails?.session_title} />
                    <DetailRow label="Ticket" value={ticketDetails?.ticket_type_label} />
                    <DetailRow label="Quantity" value={ticketDetails?.quantity} />
                  </Box>

                  <Box sx={paymentStatusStyles.ticketPanel}>
                    <Stack spacing={0.5} alignItems="center" sx={paymentStatusStyles.detailsHeader}>
                      <Typography variant="subtitle1" sx={paymentStatusStyles.detailsTitle}>{isMultiAttendee ? 'Attendees' : 'Attendee'}</Typography>
                    </Stack>
                    <Divider />
                    <Stack spacing={1.5} sx={{ mt: 2 }}>
                      {attendees.length === 0 && <Typography color="text.secondary">No attendee records found.</Typography>}
                      {attendees.map((attendee, index) => (
                        <Box key={attendee.id || `attendee-${index}`} sx={{ border: '1px solid rgba(148, 163, 184, 0.35)', borderRadius: 2, p: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{isMultiAttendee ? `Attendee ${index + 1}` : 'Attendee'}</Typography>
                          <DetailRow label="Name" value={attendee.name} />
                          <DetailRow label="Email" value={attendee.email} />
                          <DetailRow label="Phone" value={attendee.phone} />
                        </Box>
                      ))}
                    </Stack>
                  </Box>

                  {!isAlreadyPaid && (
                    <Box sx={paymentStatusStyles.ticketPanel}>
                      <Stack spacing={1.5}>
                        <Typography variant="subtitle1" sx={paymentStatusStyles.detailsTitle}>Payment Method</Typography>
                        <Grid container spacing={1.5}>
                          {paymentOptions.map((option) => {
                            const selected = paymentType === option.value;
                            return (
                              <Grid size={{ xs: 12 }} key={option.value}>
                                <Box component="label" sx={registrationDialogStyles.paymentOption(selected)}>
                                  <input
                                    type="radio"
                                    name="pendingPaymentType"
                                    value={option.value}
                                    checked={selected}
                                    onChange={(event) => {
                                      setPaymentType(event.target.value);
                                      setPaymentTypeError('');
                                    }}
                                    style={registrationDialogStyles.paymentInput}
                                  />
                                  <Stack spacing={1} sx={{ height: '100%' }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                      <Box sx={registrationDialogStyles.paymentBadge(selected)}>{option.badge}</Box>
                                      {selected && <CheckCircleIcon sx={registrationDialogStyles.paymentCheck} />}
                                    </Stack>
                                    <Typography sx={registrationDialogStyles.paymentOptionTitle}>{option.title}</Typography>
                                    <Typography variant="body2" color="text.secondary">{option.description}</Typography>
                                    <Stack direction="row" spacing={0.75} justifyContent="flex-end" useFlexGap sx={{ ...registrationDialogStyles.paymentLogoGroup, flexWrap: 'wrap' }}>
                                      {option.logos.map((logo) => (
                                        <Box key={logo.alt} sx={registrationDialogStyles.paymentLogoTile}>
                                          <Box component="img" src={logo.src} alt={logo.alt} sx={registrationDialogStyles.paymentLogo} />
                                        </Box>
                                      ))}
                                    </Stack>
                                  </Stack>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>

                        {paymentTypeError && <Alert severity="error">{paymentTypeError}</Alert>}
                        {submitError && <Alert severity="error">{submitError}</Alert>}

                        <Stack direction="row" justifyContent="flex-end">
                          <Button
                            variant="contained"
                            onClick={handleProceedToPayment}
                            disabled={submitting}
                            sx={paymentStatusStyles.primaryActionButton}
                          >
                            {submitting ? 'Starting Checkout...' : 'Proceed to Payment'}
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  )}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default CompletePendingPaymentPage;
